import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';
import { FaTimes, FaStar, FaRegStar, FaClipboardList, FaPaperPlane, FaFlask } from 'react-icons/fa';

interface PerguntaDto {
  id: string;
  texto: string;
  tipoPergunta: number;
  opcoes: string;
  ordem: number;
  obrigatoria: boolean;
}

interface PesquisaDto {
  id: string;
  titulo: string;
  descricao: string;
  perguntas: PerguntaDto[];
}

interface Props {
  pesquisa: PesquisaDto;
  onClose: () => void;
}

const slideUp = keyframes`from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); }`;

const Overlay = styled.div`
  position: fixed; inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex; align-items: center; justify-content: center;
  z-index: 1050; padding: 20px;
`;

const Modal = styled.div`
  background: #fff; border-radius: 20px;
  width: 100%; max-width: 600px;
  max-height: 85vh; overflow-y: auto;
  box-shadow: 0 25px 60px rgba(0,0,0,0.2);
  animation: ${slideUp} 0.25s ease-out;
`;

const TestBadge = styled.div`
  background: #fef3c7; color: #92400e;
  text-align: center; padding: 8px;
  font-size: 0.78rem; font-weight: 700;
  display: flex; align-items: center; justify-content: center; gap: 6px;
  border-radius: 20px 20px 0 0;
`;

const ModalHeader = styled.div`
  padding: 24px 28px 0; text-align: center;
`;

const ModalIcon = styled.div`
  width: 56px; height: 56px; border-radius: 50%;
  background: linear-gradient(135deg, #eff6ff, #dbeafe);
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto 16px; color: #3b82f6; font-size: 1.3rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem; font-weight: 700; color: #0f172a; margin: 0 0 8px;
`;

const ModalDesc = styled.p`
  font-size: 0.88rem; color: #64748b; margin: 0; line-height: 1.5;
`;

const ProgressBar = styled.div`
  margin: 20px 28px 0;
  height: 4px; background: #e2e8f0; border-radius: 2px; overflow: hidden;
`;

const ProgressFill = styled.div<{ pct: number }>`
  height: 100%; background: #3b82f6; border-radius: 2px;
  width: ${p => p.pct}%; transition: width 0.3s ease;
`;

const ModalBody = styled.div`
  padding: 24px 28px;
`;

const PerguntaLabel = styled.label`
  display: block; font-size: 0.9rem; font-weight: 600;
  color: #0f172a; margin-bottom: 10px;
`;

const Required = styled.span`color: #ef4444; margin-left: 4px;`;

const TextInput = styled.textarea`
  width: 100%; padding: 12px 14px;
  border: 1.5px solid #e2e8f0; border-radius: 10px;
  font-size: 0.9rem; min-height: 80px; resize: vertical;
  &:focus { border-color: #3b82f6; outline: none; }
`;

const StarsRow = styled.div`display: flex; gap: 8px;`;

const StarBtn = styled.button<{ active: boolean }>`
  border: none; background: none; cursor: pointer;
  font-size: 1.6rem; color: ${p => p.active ? '#f59e0b' : '#cbd5e1'};
  transition: transform 0.15s, color 0.15s;
  &:hover { transform: scale(1.15); color: #f59e0b; }
`;

const NpsRow = styled.div`display: flex; gap: 6px; flex-wrap: wrap;`;

const NpsBtn = styled.button<{ active: boolean; score: number }>`
  width: 40px; height: 40px; border-radius: 10px;
  border: 2px solid ${p => p.active ? (p.score <= 6 ? '#ef4444' : p.score <= 8 ? '#f59e0b' : '#22c55e') : '#e2e8f0'};
  background: ${p => p.active ? (p.score <= 6 ? '#fef2f2' : p.score <= 8 ? '#fffbeb' : '#f0fdf4') : '#fff'};
  color: ${p => p.active ? (p.score <= 6 ? '#ef4444' : p.score <= 8 ? '#f59e0b' : '#22c55e') : '#64748b'};
  font-weight: 700; font-size: 0.9rem;
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: #3b82f6; }
`;

const NpsLabels = styled.div`
  display: flex; justify-content: space-between;
  font-size: 0.72rem; color: #94a3b8; margin-top: 4px;
`;

const OptionBtn = styled.button<{ active: boolean }>`
  display: block; width: 100%; text-align: left;
  padding: 12px 16px; margin-bottom: 8px;
  border: 2px solid ${p => p.active ? '#3b82f6' : '#e2e8f0'};
  border-radius: 10px;
  background: ${p => p.active ? '#eff6ff' : '#fff'};
  color: ${p => p.active ? '#1e40af' : '#374151'};
  font-size: 0.88rem; font-weight: ${p => p.active ? 600 : 400};
  cursor: pointer; transition: all 0.15s;
  &:hover { border-color: #3b82f6; }
`;

const ModalFooter = styled.div`
  display: flex; justify-content: space-between; align-items: center;
  padding: 16px 28px 24px; gap: 12px;
`;

const BtnSecondary = styled.button`
  padding: 12px 24px; border: 1.5px solid #e2e8f0;
  border-radius: 10px; background: #fff;
  color: #475569; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; &:hover { background: #f8fafc; }
`;

const BtnSubmit = styled.button`
  padding: 12px 24px; border: none; border-radius: 10px;
  background: #3b82f6; color: #fff; font-size: 0.9rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px;
  &:hover { background: #2563eb; }
`;

const StepInfo = styled.span`
  font-size: 0.82rem; color: #94a3b8; font-weight: 500;
`;

const PesquisaTesteModal: React.FC<Props> = ({ pesquisa, onClose }) => {
  const [respostas, setRespostas] = useState<Record<string, string>>({});
  const [step, setStep] = useState(0);

  const perguntas = pesquisa.perguntas || [];
  const perguntaAtual = perguntas[step];
  const totalSteps = perguntas.length;
  const isLast = step === totalSteps - 1;

  if (!perguntaAtual) {
    return (
      <Overlay onClick={onClose}>
        <Modal onClick={e => e.stopPropagation()}>
          <TestBadge><FaFlask /> MODO TESTE</TestBadge>
          <ModalBody style={{ textAlign: 'center', padding: 40, color: '#94a3b8' }}>
            Esta pesquisa nao possui perguntas.
          </ModalBody>
          <ModalFooter>
            <span />
            <BtnSecondary onClick={onClose}>Fechar</BtnSecondary>
          </ModalFooter>
        </Modal>
      </Overlay>
    );
  }

  const setResposta = (id: string, valor: string, autoNext = false) => {
    setRespostas(prev => ({ ...prev, [id]: valor }));
    if (autoNext && step < totalSteps - 1) {
      setTimeout(() => setStep(s => s + 1), 300);
    }
  };

  const handleEnviar = () => {
    const obrigatorias = perguntas.filter(p => p.obrigatoria);
    const faltando = obrigatorias.find(p => !respostas[p.id]?.trim());
    if (faltando) {
      toast.warning('Responda todas as perguntas obrigatorias');
      setStep(perguntas.findIndex(p => p.id === faltando.id));
      return;
    }
    toast.success('Teste concluido! Nenhuma resposta foi enviada.');
    onClose();
  };

  const renderInput = (p: typeof perguntaAtual) => {
    const valor = respostas[p.id] || '';

    switch (p.tipoPergunta) {
      case 0:
        return <TextInput value={valor} onChange={e => setResposta(p.id, e.target.value)} placeholder="Digite sua resposta..." />;
      case 1:
        return (
          <StarsRow>
            {[1, 2, 3, 4, 5].map(s => (
              <StarBtn key={s} active={Number(valor) >= s} onClick={() => setResposta(p.id, String(s), true)}>
                {Number(valor) >= s ? <FaStar /> : <FaRegStar />}
              </StarBtn>
            ))}
          </StarsRow>
        );
      case 2: {
        const opcoes = p.opcoes ? p.opcoes.split(',').map(o => o.trim()).filter(Boolean) : [];
        return (
          <div>
            {opcoes.map(opt => (
              <OptionBtn key={opt} active={valor === opt} onClick={() => setResposta(p.id, opt, true)}>{opt}</OptionBtn>
            ))}
          </div>
        );
      }
      case 3:
        return (
          <div style={{ display: 'flex', gap: 12 }}>
            <OptionBtn active={valor === 'Sim'} onClick={() => setResposta(p.id, 'Sim', true)} style={{ flex: 1, textAlign: 'center' }}>Sim</OptionBtn>
            <OptionBtn active={valor === 'Nao'} onClick={() => setResposta(p.id, 'Nao', true)} style={{ flex: 1, textAlign: 'center' }}>Nao</OptionBtn>
          </div>
        );
      case 4:
        return (
          <>
            <NpsRow>
              {Array.from({ length: 11 }, (_, i) => i).map(n => (
                <NpsBtn key={n} active={valor === String(n)} score={n} onClick={() => setResposta(p.id, String(n), true)}>{n}</NpsBtn>
              ))}
            </NpsRow>
            <NpsLabels><span>Nada provavel</span><span>Muito provavel</span></NpsLabels>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={e => e.stopPropagation()}>
        <TestBadge><FaFlask /> MODO TESTE - Nenhuma resposta sera salva</TestBadge>
        <ModalHeader>
          <ModalIcon><FaClipboardList /></ModalIcon>
          <ModalTitle>{pesquisa.titulo}</ModalTitle>
          {pesquisa.descricao && <ModalDesc>{pesquisa.descricao}</ModalDesc>}
        </ModalHeader>

        <ProgressBar>
          <ProgressFill pct={((step + 1) / totalSteps) * 100} />
        </ProgressBar>

        <ModalBody>
          <PerguntaLabel>
            {perguntaAtual.texto}
            {perguntaAtual.obrigatoria && <Required>*</Required>}
          </PerguntaLabel>
          {renderInput(perguntaAtual)}
        </ModalBody>

        <ModalFooter>
          <StepInfo>{step + 1} de {totalSteps}</StepInfo>
          <div style={{ display: 'flex', gap: 10 }}>
            {step > 0 && <BtnSecondary onClick={() => setStep(step - 1)}>Anterior</BtnSecondary>}
            {!isLast ? (
              <BtnSubmit onClick={() => setStep(step + 1)}>Proxima</BtnSubmit>
            ) : (
              <BtnSubmit onClick={handleEnviar}>
                <FaPaperPlane /> Finalizar Teste
              </BtnSubmit>
            )}
          </div>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};

export default PesquisaTesteModal;
